import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import dayjs from 'dayjs';
import type { CalendarEvent } from './WeekTimelineView';
import EventCard from './EventCard';

interface MonthGridViewProps {
  events: CalendarEvent[];
  selectedDate: Date;
  weekStartDay?: number;
  onPressDay?: (date: Date) => void;
  onPressEvent?: (event: CalendarEvent) => void;
  onShowMore?: (date: Date) => void;
}

const PRIMARY = '#2e96e1';
const MAX_VISIBLE = 2;
const LANE_HEIGHT = 132;
const LANE_GAP = 5;

const MonthGridView: React.FC<MonthGridViewProps> = ({
  events,
  selectedDate,
  weekStartDay = 0,
  onPressDay,
  onPressEvent,
  onShowMore,
}) => {
  const today = dayjs().format('YYYY-MM-DD');
  const month = dayjs(selectedDate);

  const cells = useMemo(() => {
    const firstOfMonth = month.startOf('month');
    let leading = firstOfMonth.day() - weekStartDay;
    if (leading < 0) leading += 7;
    const gridStart = firstOfMonth.subtract(leading, 'day');
    const result: dayjs.Dayjs[] = [];
    for (let i = 0; i < 42; i++) result.push(gridStart.add(i, 'day'));
    return result;
  }, [month, weekStartDay]);

  // Single-day events bucketed by day (multi-day events handled separately as spanning bars)
  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const ev of events) {
      const s = dayjs(ev.start).startOf('day');
      const e = dayjs(ev.end).startOf('day');
      if (!s.isSame(e, 'day')) continue;
      const key = s.format('YYYY-MM-DD');
      const arr = map.get(key) || [];
      arr.push(ev);
      map.set(key, arr);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.start.getTime() - b.start.getTime());
    return map;
  }, [events]);

  // Multi-day events split into per-week segments with lane assignments
  const weeksData = useMemo(() => {
    type Segment = {
      event: CalendarEvent;
      startCol: number;
      endCol: number;
      continuesBefore: boolean;
      continuesAfter: boolean;
      lane: number;
    };
    const result: { segments: Segment[]; laneCount: number }[] = [];
    for (let w = 0; w < 6; w++) {
      const weekStart = cells[w * 7];
      const weekEnd = cells[w * 7 + 6];
      const raw: Omit<Segment, 'lane'>[] = [];
      for (const ev of events) {
        const evStart = dayjs(ev.start).startOf('day');
        const evEnd = dayjs(ev.end).startOf('day');
        if (evStart.isSame(evEnd, 'day')) continue;
        if (evEnd.isBefore(weekStart, 'day') || evStart.isAfter(weekEnd, 'day')) continue;
        const startCol = Math.max(0, evStart.diff(weekStart, 'day'));
        const endCol = Math.min(6, evEnd.diff(weekStart, 'day'));
        raw.push({
          event: ev,
          startCol,
          endCol,
          continuesBefore: evStart.isBefore(weekStart, 'day'),
          continuesAfter: evEnd.isAfter(weekEnd, 'day'),
        });
      }
      // Greedy lane allocation: longer/earlier first
      raw.sort((a, b) => a.startCol - b.startCol || (b.endCol - b.startCol) - (a.endCol - a.startCol));
      const laneEnds: number[] = [];
      const segments: Segment[] = raw.map(seg => {
        let lane = laneEnds.findIndex(end => end < seg.startCol);
        if (lane === -1) {
          lane = laneEnds.length;
          laneEnds.push(seg.endCol);
        } else {
          laneEnds[lane] = seg.endCol;
        }
        return { ...seg, lane };
      });
      result.push({ segments, laneCount: laneEnds.length });
    }
    return result;
  }, [cells, events]);

  const weekHeaders = useMemo(() => {
    const r: string[] = [];
    for (let i = 0; i < 7; i++) {
      r.push(dayjs().day((weekStartDay + i) % 7).format('ddd').toUpperCase());
    }
    return r;
  }, [weekStartDay]);

  return (
    <View style={styles.container}>
      <View style={styles.weekHeaderRow}>
        {weekHeaders.map((d, i) => (
          <View key={i} style={styles.weekHeaderCell}>
            <Text style={styles.weekHeaderText}>{d}</Text>
          </View>
        ))}
      </View>
      <ScrollView showsVerticalScrollIndicator={false}>
        <View style={styles.grid}>
          {Array.from({ length: 6 }).map((_, weekIdx) => {
            const wk = weeksData[weekIdx];
            const lanesTopOffset = 32; // space for day number row before lanes start
            const lanesHeight = wk.laneCount * (LANE_HEIGHT + LANE_GAP);
            const cellTopPadding = lanesTopOffset + lanesHeight + 4;
            return (
            <View key={weekIdx} style={styles.weekRow}>
              {cells.slice(weekIdx * 7, weekIdx * 7 + 7).map((day, idx) => {
                const key = day.format('YYYY-MM-DD');
                const inMonth = day.month() === month.month();
                const isToday = key === today;
                const dayEvents = eventsByDay.get(key) || [];
                const visible = dayEvents.slice(0, MAX_VISIBLE);
                const overflow = dayEvents.length - visible.length;
                return (
                  <TouchableOpacity
                    key={idx}
                    style={[styles.cell, !inMonth && styles.cellOutsideMonth, { paddingTop: cellTopPadding }]}
                    onPress={() => onPressDay?.(day.hour(9).minute(0).second(0).toDate())}
                    activeOpacity={0.7}
                  >
                    <View style={[styles.dayNumberRow, styles.dayNumberRowAbsolute]}>
                      <View style={[styles.dayNumberWrap, isToday && styles.dayNumberWrapToday]}>
                        <Text
                          style={[
                            styles.dayNumber,
                            !inMonth && styles.dayNumberOutside,
                            isToday && styles.dayNumberToday,
                          ]}
                        >
                          {day.format('D')}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.eventStack}>
                      {visible.map(ev => (
                        <EventCard
                          key={ev.id}
                          event={ev}
                          density="compact"
                          onPress={() => onPressEvent?.(ev)}
                          style={styles.eventCardSpacing}
                        />
                      ))}
                      {overflow > 0 && (
                        <TouchableOpacity
                          onPress={() => onShowMore?.(day.toDate())}
                          activeOpacity={0.6}
                        >
                          <Text style={styles.moreText}>+{overflow} more</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  </TouchableOpacity>
                );
              })}
              {/* Multi-day spanning bars (unified EventCard style) */}
              {wk.segments.map((seg, sIdx) => {
                const widthPct = ((seg.endCol - seg.startCol + 1) / 7) * 100;
                const leftPct = (seg.startCol / 7) * 100;
                const top = lanesTopOffset + seg.lane * (LANE_HEIGHT + LANE_GAP);
                return (
                  <View
                    key={`${seg.event.id}-${sIdx}`}
                    style={{
                      position: 'absolute',
                      top,
                      height: LANE_HEIGHT,
                      left: `${leftPct}%` as any,
                      width: `${widthPct}%` as any,
                      paddingLeft: seg.continuesBefore ? 0 : 3,
                      paddingRight: seg.continuesAfter ? 0 : 3,
                    }}
                  >
                    <EventCard
                      event={seg.event}
                      density="compact"
                      continuesBefore={seg.continuesBefore}
                      continuesAfter={seg.continuesAfter}
                      onPress={() => onPressEvent?.(seg.event)}
                      style={styles.spanFill}
                    />
                  </View>
                );
              })}
            </View>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  weekHeaderRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  weekHeaderCell: {
    flex: 1,
    paddingVertical: 8,
    alignItems: 'center',
  },
  weekHeaderText: { fontSize: 14, color: '#444', letterSpacing: 0.5, fontWeight: '700' },
  grid: {},
  weekRow: {
    flexDirection: 'row',
    position: 'relative',
  },
  dayNumberRowAbsolute: {
    position: 'absolute',
    top: 5,
    right: 5,
    left: 5,
    zIndex: 0,
  },
  cell: {
    flex: 1,
    minHeight: 200,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#E0E0E0',
    padding: 6,
  },
  cellOutsideMonth: { backgroundColor: '#FAFAFA' },
  dayNumberRow: { flexDirection: 'row', justifyContent: 'flex-end' },
  chipDotsRow: { flexDirection: 'row', alignItems: 'center', marginLeft: 4 },
  chipDot: { width: 9, height: 9, borderRadius: 4.5, marginLeft: 3 },
  dayNumberWrap: {
    minWidth: 28,
    height: 28,
    borderRadius: 14,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dayNumberWrapToday: { backgroundColor: PRIMARY },
  dayNumber: { fontSize: 15, color: '#111', fontWeight: '700' },
  dayNumberOutside: { color: '#BBB' },
  dayNumberToday: { color: '#fff' },
  eventStack: { marginTop: 6 },
  eventCardSpacing: { marginBottom: 5 },
  spanFill: { flex: 1 },
  moreText: { fontSize: 13, fontWeight: '700', color: PRIMARY, marginTop: 4, paddingHorizontal: 4 },
});

export default MonthGridView;
